import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { Environment } from '~/engine/data/Environment';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { getPreviewColor } from '../margin/marginToolbarUtils';
import { domainEventBus, RoiColorChangeRequestedEvent } from '~/domain/events';

interface UseGetOrCreateDestinationOptions {
    environment: Environment | null;
}

/**
 * Shared hook that either looks up an existing output ROI (by roiNumber) or
 * creates a new one (with the given name), applying a derived preview color
 * (seeded from an optional input ROI) to newly-created structures so the
 * rendering engine and domain model stay in sync.
 *
 * `inputRoi` is a callback argument rather than a hook option so callers
 * that resolve the input ROI dynamically (e.g. by name at request time,
 * as the chat-driven flow does) can reuse this hook.
 */
export function useGetOrCreateDestination({
    environment,
}: UseGetOrCreateDestinationOptions) {
    const intl = useIntl();
    const { createNewRoi } = useStructureSet();

    const getOrCreateDestination = useCallback(
        async (
            outputRoiNumber: number | null,
            newName?: string,
            inputRoi?: RegionOfInterest
        ) => {
            const { structureSet } = environment || {};
            if (!structureSet) {
                return null;
            }

            if (outputRoiNumber === null) {
                if (!newName) {
                    throw new Error(
                        'newName is required when creating a new ROI'
                    );
                }
                const newRoi = await createNewRoi(
                    structureSet.sopInstanceUID,
                    newName
                );
                if (!newRoi) {
                    enqueueSnackbar('Failed to create new ROI', {
                        variant: 'error',
                    });
                    return null;
                }
                const dest = structureSet.getRoi(newRoi.roiNumber);
                if (!dest) {
                    enqueueSnackbar(`Structure "${newName}" not found`, {
                        variant: 'error',
                    });
                    return null;
                }

                // Set the new structure's color via the domain event so
                // both the domain model (Recoil/structure panel) and the
                // rendering engine (RegionOfInterest) stay in sync.
                if (inputRoi) {
                    const previewCol = getPreviewColor(inputRoi.color);
                    await domainEventBus.publish(
                        new RoiColorChangeRequestedEvent(
                            dest.roiNumber,
                            {
                                R: Math.round(previewCol[0] * 255),
                                G: Math.round(previewCol[1] * 255),
                                B: Math.round(previewCol[2] * 255),
                            },
                            'system'
                        )
                    );
                }

                return dest;
            }

            const dest = structureSet.getRoi(outputRoiNumber);
            if (!dest) {
                enqueueSnackbar(
                    `Structure with ROI number ${outputRoiNumber} not found`,
                    { variant: 'error' }
                );
                return null;
            }

            if (!dest.canModify) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'structureCannotBeModified' }),
                    { variant: 'error' }
                );
                return null;
            }

            return dest;
        },
        [environment, createNewRoi, intl]
    );

    return { getOrCreateDestination };
}
